import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { useAutumnSubscription } from '@/hooks/useAutumnSubscription';

interface RequireSubscriptionProps {
  children: ReactNode;
  redirectTo?: string;
}

const RequireSubscription = ({
  children,
  redirectTo = '/subscription'
}: RequireSubscriptionProps) => {
  const { user } = useAuth();
  const { hasAccess, isLoading, hasLegacyLifetimeAccess } = useAutumnSubscription();

  // If not authenticated at all, redirect to login
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Show loading state while checking subscription
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
        <p className="text-sm text-muted-foreground">Checking access...</p>
      </div>
    );
  }

  // Check if user has access via:
  // 1. Legacy lifetime access (from $19 one-time purchase)
  // 2. Monthly subscription (includes 'trialing' status from Stripe)
  // 3. Yearly subscription (includes 'trialing' status from Stripe)
  // 4. Autumn lifetime product (if configured)
  // Note: Autumn's hasAccess() automatically includes users with status 'trialing' or 'active'
  const hasValidAccess =
    hasLegacyLifetimeAccess() ||    // Previous $19 customers
    hasAccess("monthly") ||         // Monthly subscribers (includes trial)
    hasAccess("yearly") ||          // Yearly subscribers (includes trial)
    hasAccess("lifetime");          // Autumn lifetime product (if configured)

  // If no access, redirect to subscription page
  if (!hasValidAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  // Render children if user has access
  return <>{children}</>;
};

export default RequireSubscription; 